// src/App.js

import React from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { ThemeProvider } from "styled-components";
import MainLayout from "./components/templates/MainLayout";
import HomePage from "./components/pages/HomePage";
import ViewAllChallengesPage from "./components/pages/ViewAllChallengesPage";
import ViewAllSubmissionsPage from "./components/pages/ViewAllSubmissionsPage";
import UserProfile from "./components/pages/UserProfile";
import ChallengeDetailPage from "./components/pages/ChallengeDetailPage";
import SubmissionDetailPage from "./components/pages/SubmissionDetailPage";
import SignInPage from "./components/pages/SignInPage";
import SignUpPage from "./components/pages/SignUpPage";
import ChallengeCreatorPage from "./components/pages/ChallengeCreator/ChallengeCreatorPage";
import ChallengeSubmissionPage from "./components/pages/ChallengeSubmissionPage";
import SuccessPage from "./components/pages/ChallengeSubmission/SuccessPage";
import FailurePage from "./components/pages/ChallengeSubmission/FailurePage";
import ProfileEditPage from "./components/pages/ProfileEditPage";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import { theme } from "./theme";
import GlobalStyles from "./GlobalStyles";
import { PayPalScriptProvider } from "@paypal/react-paypal-js";
import ViewAllSubmissionContributionsPage from "./components/pages/ViewAllSubmissionContributions";
// import SubmissionMigrationPage from "./components/pages/SubmissionMigrationPage";
import SocialVideo from "./components/pages/SocialVideo";
import VideoInteractions from "./components/pages/VideoInteractions";
import CAPMembershipPage from "./components/pages/CAPMembershipPage";
import WatchingPage from "./components/pages/WatchingPage";
import HoldingDetailPage from "./components/pages/HoldingDetailPage";
import EventAdminPage from "./components/pages/EventManager/EventAdminPage";
import EventContentPage from "./components/pages/EventManager/EventContentPage";
import EventListPage from "./components/pages/EventManager/EventListPage";
import EventDetailPage from "./components/pages/EventManager/EventDetailPage";
import SubmissionsPage from "./components/pages/Admin/SubmissionsPage";
import UploadArtworkPage from "./components/pages/UploadArtworkPage";
import CreatorsAllianceProgramPage from "./components/pages/CreatorsAllianceProgramPage";
import MarketPlace from "./components/pages/MarketPlace";
import Creator from "./components/pages/MarketPlace/Creator";
import Fan from "./components/pages/MarketPlace/Fan";
import EventDetail from "./components/pages/MarketPlace/EventDetail";
import ArtistProfile from "./components/pages/MarketPlace/ArtistProfile";
import Artworks from "./components/pages/MarketPlace/Artworks";
import EventWatchPage from "./components/pages/events/EventWatchPage";
// Kept for the disabled draft-submissions route below — see the note there.
// eslint-disable-next-line no-unused-vars
import AdminDraftSubmissionsPage from "./components/pages/Admin/AdminDraftSubmissionsPage";
// import UserProfileAuditPage from "./components/pages/Admin/UserProfileAuditPage";

const App = () => {
  return (
    <PayPalScriptProvider
      options={{ "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID }}
    >
      <ThemeProvider theme={theme}>
        <GlobalStyles /> {/* Apply GlobalStyles */}
        <Router>
          <MainLayout>
            <Routes>
              {/* <Route
                path="/challenge/submission/migration"
                element={
                  <ProtectedRoute>
                    <SubmissionMigrationPage />
                  </ProtectedRoute>
                }
              /> */}

              <Route path="/challenge/" element={<HomePage />} />

              {/* Route for uploading a new artwork */}
              <Route
                path="/challenge/upload-artwork"
                element={
                  <ProtectedRoute>
                    <UploadArtworkPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/:challengeId"
                element={<ChallengeDetailPage />}
              />

              <Route
                path="/challenge/all/"
                element={<ViewAllChallengesPage />}
              />

              <Route
                path="/challenge/submissions/all/"
                element={<ViewAllSubmissionsPage />}
              />

              <Route
                path="/challenge/submissions/all/:challengeId"
                element={<ViewAllSubmissionsPage />}
              />

              <Route
                path="/challenge/submissions/all/:userId"
                element={<ViewAllSubmissionsPage />}
              />

              <Route
                path="/challenge/submission/:submissionId"
                element={<SubmissionDetailPage />}
              />

              <Route
                path="/challenge/submission/contributions/:submissionId/amount/:contributionAmount"
                element={
                  <ProtectedRoute>
                    <ViewAllSubmissionContributionsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/submission/contributions/"
                element={
                  <ProtectedRoute>
                    <ViewAllSubmissionContributionsPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/contributions/view"
                element={
                  <ProtectedRoute>
                    <HoldingDetailPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/challenge/signin" element={<SignInPage />} />
              <Route path="/challenge/connect" element={<SignInPage />} />
              <Route path="/challenge/signup" element={<SignUpPage />} />

              {/* Route for /profile with and without profileId */}
              <Route path="/challenge/profile" element={<UserProfile />} />
              <Route
                path="/challenge/profile/:profileId"
                element={<UserProfile />}
              />
              <Route
                path="/challenge/profile/username/:username"
                element={<UserProfile />}
              />
              <Route
                path="/challenge/profile/:profileId/challenges"
                element={<ViewAllChallengesPage />}
              />

              <Route
                path="/challenge/create"
                element={
                  <ProtectedRoute>
                    <ChallengeCreatorPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/challenge/edit/:challengeId"
                element={
                  <ProtectedRoute>
                    <ChallengeCreatorPage />
                  </ProtectedRoute>
                }
              />

              {/* Route for new submission (no submissionId) */}
              <Route
                path="/challenge/:challengeId/submission/new"
                element={
                  <ProtectedRoute>
                    <ChallengeSubmissionPage />
                  </ProtectedRoute>
                }
              />

              {/* Route for editing existing submission */}
              <Route
                path="/challenge/:challengeId/submission/:submissionId"
                element={
                  <ProtectedRoute>
                    <ChallengeSubmissionPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/profile/edit"
                element={
                  <ProtectedRoute>
                    <ProfileEditPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/submission/purchase/success"
                element={
                  <ProtectedRoute>
                    <SuccessPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/submission/purchase/failure"
                element={
                  <ProtectedRoute>
                    <FailurePage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/videos/"
                element={
                  <ProtectedRoute>
                    <SocialVideo />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/challenge/videos/watching"
                element={
                  <ProtectedRoute>
                    <WatchingPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="challenge/videos/:videoId/interactions"
                element={
                  <ProtectedRoute>
                    <VideoInteractions />
                  </ProtectedRoute>
                }
              />

              <Route
                path="challenge/memberships/cap"
                element={
                  <ProtectedRoute>
                    <CAPMembershipPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="/creators-alliance-program"
                element={
                  <ProtectedRoute>
                    <CreatorsAllianceProgramPage />
                  </ProtectedRoute>
                }
              />

              <Route
                path="challenge/admin/events"
                element={
                  <ProtectedRoute>
                    <EventAdminPage />
                  </ProtectedRoute>
                }
              />

              {/* DISABLED: the admin draft-submissions route is commented out,
                  which is why its import above is reported unused. Restore both
                  together to bring the page back. */}
              {/* <Route
                path="challenge/admin/draft-submissions"
                element={
                  <ProtectedRoute>
                    <AdminDraftSubmissionsPage />
                  </ProtectedRoute>
                }
              /> */}

              {/* <Route
                path="challenge/admin/profile-audit"
                element={
                  <ProtectedRoute>
                    <UserProfileAuditPage />
                  </ProtectedRoute>
                }
              /> */}

              <Route
                path="/challenge/admin/events/content"
                element={
                  <ProtectedRoute>
                    <EventContentPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/challenge/events" element={<EventListPage />} />
              <Route path="/challenge/events/:eventId" element={<EventDetailPage />} />
              <Route path="/challenge/events/:eventId/watch" element={<EventWatchPage />} />

              <Route
                path="challenge/admin/submissions"
                element={
                  <ProtectedRoute>
                    <SubmissionsPage />
                  </ProtectedRoute>
                }
              />

              <Route path="/marketplace" element={<MarketPlace />} />
              <Route path="/marketplace/artworks" element={<ProtectedRoute><Artworks /></ProtectedRoute>} />
              <Route path="/marketplace/creators" element={<ProtectedRoute><Creator /></ProtectedRoute>} />
              <Route path="/marketplace/fan" element={<Fan />} />
              <Route path="/marketplace/event/detail" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
              <Route path="/marketplace/artistProfile/:username" element={<ProtectedRoute><ArtistProfile /></ProtectedRoute>} />

            </Routes>
          </MainLayout>
        </Router>
      </ThemeProvider>
    </PayPalScriptProvider>
  );
};

export default App;
